// Which offer detail pages are allowed into Google's index.
//
// This is a RUNTIME decision, driven by the offer's own data rather than a
// build-time list, and that is the whole point: the site is prerendered at
// build time, but a URL with no prerendered file is served the SPA shell with
// HTTP 200 and rendered client-side. A data-driven rule therefore means a
// newly published offer classifies itself correctly with no rebuild and no
// redeploy — publishing an offer is enough.
//
// Why not simply index every offer page: as a block of 300+ thin pages
// (a supplier brand fragment as the title + a price + an affiliate link) they
// made the whole domain look like a thin-affiliate site, which suppressed the
// blog as well. Google had indexed only ~26 of ~308 discovered pages. Offers
// that DO carry a real product name and a substantial description are genuine
// search targets and are worth indexing on their own.
//
// Thresholds calibrated against the live catalogue (332 offers): 113 qualify,
// 219 stay noindex. Excluded examples: "SZN", "Gamyam", "Indo Era" (supplier
// names, each also duplicated across several offers), "Puma Hustle sneakers
// for men" (real name but no description), and "Upto 80% Off Across
// Categories" (generic promo — there is no product to search for).
//
// On the description threshold specifically: it is a weak lever and not worth
// re-tuning. Measured sweep over the same catalogue, holding the title rules
// constant — 400ch: 81 pages, 300ch: 94, 250ch: 100, 200ch: 113, 150ch: 124,
// and dropping the requirement entirely still only reaches 159. The real gate
// is the TITLE rule: 173 of 332 offers fail it on their own (169 have a title
// under 25 chars). If more indexable pages are wanted, fix titles and write
// descriptions in the admin panel — that needs no code change and no deploy,
// because this rule is evaluated at render time from live API data.
//
// KEEP IN SYNC with `isOfferIndexable` in scripts/seo-lib.js, which applies
// the same rule when generating the fallback sitemap. CRA's src/ cannot import
// from scripts/ (ESM vs CommonJS), hence the deliberate duplication — the same
// arrangement already used for toSlug/buildSlug.

export const MIN_DESCRIPTION_LENGTH = 200;
export const MIN_TITLE_LENGTH = 25;

// "Upto 80% Off Across Categories", "UP TO 91% OFF | Flipkart Smartbuy Wall
// Clocks" — promotional phrasing with no searchable product.
const GENERIC_TITLE = /^(?:up\s?to|upto|flat|min(?:imum)?|save|grab|get)\b.*\b(?:off|discount|deals?|sale|cashback)\b/i;

export const ROBOTS_INDEXABLE = 'index, follow, max-image-preview:large, max-snippet:-1';

// `follow` even when noindex: crawlers still pass through these pages to
// /offers and /store/*, which stay indexable.
export const ROBOTS_NOINDEX = 'noindex, follow';

// How many offers a category hub (/offers/<type>) must actually hold before we
// let Google index it.
//
// Same reasoning as the offer rule above, one level up: a hub page listing one
// or two offers is a thin page, and publishing seven of them would re-create
// the exact problem the offer rule was written to fix. Live counts at the time
// of writing (332 offers): Fashion 145, Shopping 122, Electronics 53, Food 6,
// Card Offer 4, Travel 1, Others 1 — so four hubs qualify today.
//
// The hub page still RENDERS for every known category; the threshold only
// decides the robots tag. And because it is evaluated at render time from live
// API data, a category promotes itself to indexable as soon as it fills up —
// no code change, no redeploy.
//
// KEEP IN SYNC with MIN_CATEGORY_OFFERS_FOR_INDEX in scripts/seo-lib.js.
export const MIN_CATEGORY_OFFERS_FOR_INDEX = 5;

export function isOfferIndexable(offer) {
  if (!offer) return false;
  const title = String(offer.title || '').trim();
  const description = String(offer.description || '').trim();
  if (title.length < MIN_TITLE_LENGTH) return false;
  if (GENERIC_TITLE.test(title)) return false;
  return description.length >= MIN_DESCRIPTION_LENGTH;
}

export function robotsForOffer(offer) {
  return isOfferIndexable(offer) ? ROBOTS_INDEXABLE : ROBOTS_NOINDEX;
}
